<!--
  BridgerBlox.vue

  2025-07-24: Added form validation for cardholder name (first+last) and required address fields
  2025-10-20: Added Apple Pay integration with BridgerPay wallet support
-->
<template>
  <div class="d-flex flex-column align-center">
    <iframe
      v-if="iframeUrl"
      :src="iframeUrl"
      class="bridger-iframe"
      frameborder="0"
      allow="payment"
    ></iframe>

    <!-- Apple Pay Button Container - Above the divider -->
    <div
      v-if="applePayAvailable && !iframeUrl"
      class="apple-pay-container w-100"
    >
      <!-- BridgerPay launcher will inject wallet buttons here -->
      <div id="bridgerpay-wallet-container"></div>
      <div class="wallet-divider">
        <span>Or pay with</span>
      </div>
    </div>

    <form v-if="!iframeUrl" id="payment-form" class="w-100">
      <label>Card Holder Name</label>
      <div class="mb-4">
        <input
          id="cardholder-name"
          class="field w-100"
          placeholder="John Doe"
        />
      </div>

      <label>Email</label>
      <div class="mb-4">
        <input
          id="email"
          class="field w-100"
          placeholder="john.doe@example.com"
        />
      </div>

      <label>Phone</label>
      <div class="mb-4">
        <phone-input
          :value.sync="phone"
          :error.sync="showPhoneError"
          :initial-phone="initialPhone"
        />
      </div>
      <template v-if="requireAddressFields">
        <label>Address</label>
        <div class="mb-4">
          <input
            id="address1"
            v-model="address1"
            class="field w-100"
            placeholder="123 Main St"
          />
        </div>

        <div class="field-set d-flex gap-3">
          <div class="flex-1">
            <label>City</label>
            <div class="mb-4">
              <input
                id="city"
                v-model="city"
                class="field w-100"
                placeholder="New York"
              />
            </div>
          </div>

          <div class="flex-1">
            <label>ZIP Code</label>
            <div class="mb-4">
              <input
                id="zip_code"
                v-model="zipCode"
                class="field w-100"
                placeholder="10001"
              />
            </div>
          </div>
        </div>
        <div class="field-set d-flex gap-3">
          <div class="flex-1">
            <label>Country</label>
            <div class="mb-4">
              <select
                id="country"
                v-model="country"
                class="field w-100"
                @change="onCountryChange"
              >
                <option value="">Select Country</option>
                <option
                  v-for="(name, code) in countries"
                  :key="code"
                  :value="code"
                >
                  {{ name }}
                </option>
              </select>
            </div>
          </div>

          <div class="flex-1">
            <label>State/Province</label>
            <div class="mb-4">
              <select
                id="state"
                v-model="state"
                class="field w-100"
                :disabled="!showStateField"
              >
                <option value="">Select State</option>
                <option
                  v-for="(name, code) in stateOptions"
                  :key="code"
                  :value="code"
                >
                  {{ name }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </template>

      <label>Card Number</label>
      <div id="card-number" class="field mb-4">
        <div class="blox-input"></div>
      </div>

      <div class="field-set d-flex gap-3">
        <div class="flex-1">
          <label>Expiration Date</label>
          <div id="exp-date" class="field">
            <div class="input"></div>
          </div>
        </div>

        <div class="flex-1">
          <label>CVV</label>
          <div id="cvv" class="field w-100">
            <div class="input"></div>
          </div>
        </div>
      </div>
      <div v-if="formError" class="error--text text-caption mt-1">
        {{ formError }}
      </div>

      <slot />

      <v-btn
        class="mt-8"
        type="submit"
        block
        depressed
        color="success"
        :loading="processing"
        :disabled="submitDisabled"
        >Confirm & Pay</v-btn
      >
    </form>
  </div>
</template>

<script>
import { loadScript } from '@/utils/script-loader'
import PhoneInput from '@/components/common/phone-input.vue'
import axios from 'axios'
import { COUNTRIES, US_STATES, CA_PROVINCES, AU_STATES } from '@/consts'
import {
  checkCountryRestrictions,
  BLACKLISTED_COUNTRIES,
} from '@/utils/countryCheck'

export default {
  name: 'BridgerBlox',
  components: {
    PhoneInput,
  },

  props: {
    sessionToken: {
      type: String,
      required: true,
    },
    intentId: {
      type: String,
      required: true,
    },
    initialName: {
      type: String,
      default: '',
    },
    initialEmail: {
      type: String,
      default: '',
    },
    initialPhone: {
      type: String,
      default: '',
    },
    requireAddressFields: {
      type: Boolean,
      default: false,
    },
    submitDisabled: {
      type: Boolean,
      default: false,
    },
    showApplePay: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cardHolderName: '',
      email: '',
      processing: false,
      errorMessage: '',
      showRetry: false,
      hidePayment: false,
      bloxController: null,
      isValid: false,
      phone: '',
      showPhoneError: false,
      formError: null,
      iframeUrl: null,
      validationData: null,
      encrypted: null,
      address1: '',
      city: '',
      zipCode: '',
      country: '',
      state: '',
      countries: {},
      usStates: US_STATES,
      caProvinces: CA_PROVINCES,
      auStates: AU_STATES,
      applePayAvailable: false,
      applePayCashierKey: null,
      applePayCashierToken: null,
      applePayEventListeners: [],
    }
  },

  async mounted() {
    // Check country restrictions for tenant_id 80, 174 or development mode
    const isDev = process.env.NODE_ENV === 'development'
    const CHECKED_TENANTS = [8, 80, 174]
    const tenantId = this.$store.state.user?.configuration?.tenant_id

    if (CHECKED_TENANTS.includes(tenantId) || isDev) {
      const { isBlocked } = await checkCountryRestrictions()
      if (isBlocked) {
        console.log('Country is blocked, triggering payment failed')
        this.$emit('payment-failed')
        return this.$router.push('/404')
      }
    }

    // Filter out blacklisted countries from the dropdown if restrictions apply
    const shouldFilterCountries = CHECKED_TENANTS.includes(tenantId) || isDev
    this.countries = Object.fromEntries(
      COUNTRIES.filter(
        country =>
          !shouldFilterCountries ||
          !BLACKLISTED_COUNTRIES.includes(country.code_2)
      ).map(country => [country.code_2, country.label])
    )
    await this.initializeBlox()
    document.getElementById('cardholder-name').value = this.initialName
    document.getElementById('email').value = this.initialEmail

    if (this.showApplePay) {
      await this.checkApplePayAvailability()
      if (this.applePayAvailable) await this.initializeApplePay()
    }

    console.log('🔍 Final Apple Pay state:', {
      applePayAvailable: this.applePayAvailable,
      iframeUrl: this.iframeUrl,
      shouldShowButton: this.applePayAvailable && !this.iframeUrl,
    })

    // Add iframe event listeners
    window.addEventListener('message', this.handlePaymentIframeEvent)

    document
      .getElementById('payment-form')
      .addEventListener('submit', async event => {
        event.preventDefault()
        this.formError = null

        if (this.bloxController.isAllValid === false) {
          this.bloxController.validateAll()
          return
        }
        this.cardHolderName = document.getElementById('cardholder-name').value
        this.email = document.getElementById('email').value
        if (!this.cardHolderName)
          return (this.formError = 'Please enter a Card Holder Name')

        // Validate cardholder name has first and last name (two items split by space)
        const nameParts = this.cardHolderName.trim().split(' ')
        if (nameParts.length < 2)
          return (this.formError = 'Please enter both first and last name')

        if (!this.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email))
          return (this.formError = 'Please enter a valid email address')
        if (!this.phone.isValid)
          return (this.formError = 'Phone enter a valid phone number')

        // Validate address fields if required
        if (this.requireAddressFields) {
          if (!this.address1.trim())
            return (this.formError = 'Please enter a valid address')
          if (!this.city.trim())
            return (this.formError = 'Please enter a valid city')
          if (!this.zipCode.trim())
            return (this.formError = 'Please enter a valid ZIP code')
          if (!this.country) return (this.formError = 'Please select a country')
          if (this.showStateField && !this.state)
            return (this.formError = 'Please select a state/province')
        }

        try {
          this.processing = true
          this.encrypted = await this.bloxController.createPaymentCardToken({
            cardHolderName: document.getElementById('cardholder-name').value, // This field is mandatory
            singleUse: false,
          })
          this.collectFingerprint()
        } catch (error) {
          console.error(error)
          const errorMsg =
            typeof error === 'string'
              ? error
              : error?.result?.[0]?.message ||
                error?.response?.message ||
                error?.message ||
                'Failed to process card'
          this.formError = errorMsg
          this.processing = false
        }
      })
  },

  beforeDestroy() {
    if (this.bloxController) {
      this.bloxController.clearAll()
    }
    window.removeEventListener('message', this.handlePaymentIframeEvent)

    // Clean up Apple Pay event listeners
    this.applePayEventListeners.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler)
    })
  },

  computed: {
    showStateField() {
      return ['US', 'CA', 'AU'].includes(this.country)
    },
    stateOptions() {
      if (this.country === 'US') return this.usStates
      if (this.country === 'CA') return this.caProvinces
      if (this.country === 'AU') return this.auStates
      return {}
    },
  },

  methods: {
    onCountryChange() {
      if (!this.showStateField) {
        this.state = ''
      }
    },
    collectFingerprint() {
      axios
        .get('/api/bridgerpay/fingerprint', {
          params: {
            card_bin: this.encrypted.card.bin,
            payment_intent_id: this.intentId,
          },
        })
        .then(response => {
          const fingerprintIframe = document.createElement('iframe')
          fingerprintIframe.setAttribute('style', 'display: none;')
          fingerprintIframe.srcdoc = response.data
          document.body.prepend(fingerprintIframe)
        })
    },
    createPurchase(fingerprint) {
      axios
        .post('/api/bridgerpay/purchase', {
          token: this.encrypted.token,
          encrypted_cvv: this.encrypted.encryptedCvv,
          payment_intent_id: this.intentId,
          full_name: this.cardHolderName,
          email: this.email,
          phone: this.phone.formattedNumber,
          address1: this.address1,
          city: this.city,
          zip_code: this.zipCode,
          country: this.country,
          state: this.state,
          fingerprint,
          card_details: {
            token: this.encrypted.token,
            last4_digits: this.encrypted.card.last4Digits,
            brand: this.encrypted.card.brand,
            expire_month: this.encrypted.card.expireMonth,
            expire_year: this.encrypted.card.expireYear,
            address1: this.address1,
            city: this.city,
            zip_code: this.zipCode,
            country: this.country,
            state: this.state,
            card_country: this.encrypted.card.country,
          },
        })
        .catch(error => {
          this.formError = error?.response?.data?.error
          this.processing = false
        })
        .then(response => {
          if (response.data.url) {
            this.iframeUrl = response.data.url
            this.validationData = response.data
          } else {
            this.$emit('payment-success', {
              user: { email: this.email },
            })
          }
        })
    },

    handlePaymentIframeEvent(event) {
      if (event.data.event == '[bp]:fingerprint') {
        this.createPurchase(event.data.fingerprint)
      }
      if (
        (event.data.type === 'approved' || event.data.type === 'authorized') &&
        this.iframeUrl
      ) {
        this.iframeUrl = null
        this.$emit('payment-processing')
        axios
          .post('/api/bridgerpay/capture', {
            payment_intent_id: this.intentId,
          })
          .then(() => {
            this.$emit('payment-success', {
              user: { email: this.email },
            })
          })
      }
    },
    async initializeBlox() {
      try {
        await loadScript('https://checkout.bridgerpay.com/sdk')
        this.bloxController = new window.BP.BloxController({
          token: this.sessionToken,
          style: {
            base: {
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
            },
            invalid: {
              color: '#ff6666',
            },
          },
        })

        const cardNumber = this.bloxController.createBlox('cardNumber', {
          placeholder: this.$t('Card Number'),
        })

        const cardExpiryDate = this.bloxController.createBlox(
          'cardExpiryDate',
          { placeholder: 'MM/YY' }
        )

        const cardCvv = this.bloxController.createBlox('cardCvv', {
          placeholder: this.$t('CVV'),
        })
        await Promise.all([
          cardNumber.mount('#card-number'),
          cardExpiryDate.mount('#exp-date'),
          cardCvv.mount('#cvv'),
        ])

        this.setupValidationListeners(cardNumber, cardExpiryDate, cardCvv)
      } catch (error) {
        console.error('Failed to initialize Blox:', error)
      }
    },

    setupValidationListeners(cardNumber, cardExpiryDate, cardCvv) {
      const validateForm = ({ error }) => {
        if (error) this.formError = error
      }

      cardNumber.on('validate', validateForm)
      cardExpiryDate.on('validate', validateForm)
      cardCvv.on('validate', validateForm)
    },

    async checkApplePayAvailability() {
      try {
        // Load Apple Pay SDK dynamically
        await loadScript(
          'https://applepay.cdn-apple.com/jsapi/v1.3.1/apple-pay-sdk.js'
        )

        // Check if Apple Pay is available
        if (
          window.ApplePaySession &&
          window.ApplePaySession.canMakePayments()
        ) {
          console.log('✅ Apple Pay is available on this device')
          this.applePayAvailable = true
        } else {
          console.log('❌ Apple Pay not available:', {
            hasApplePaySession: !!window.ApplePaySession,
            canMakePayments: window.ApplePaySession?.canMakePayments(),
          })
        }
      } catch (error) {
        console.error('❌ Failed to load Apple Pay SDK:', error)
      }
    },

    async initializeApplePay() {
      try {
        console.log('🍎 Initializing Apple Pay...')
        const email =
          document.getElementById('email')?.value || this.initialEmail

        console.log('📞 Calling /api/bridgerpay/apple-pay/session with:', {
          payment_intent_id: this.intentId,
          country: this.country || 'US',
        })

        const response = await axios.post('/api/bridgerpay/apple-pay/session', {
          payment_intent_id: this.intentId,
          country: this.country || 'US',
          state: this.state,
          address: this.address1,
          city: this.city,
          zip_code: this.zipCode,
          phone: this.phone?.formattedNumber,
          email: email,
          shipping_contact_required: false,
        })

        console.log('✅ Apple Pay session created:', response.data)

        this.applePayCashierKey = response.data.cashier_key
        this.applePayCashierToken = response.data.cashier_token

        console.log('📦 Loading Apple Pay launcher...')
        this.setupApplePayEventListeners()
        await this.loadApplePayLauncher()

        console.log('🎧 Setting up event listeners...')

        console.log('✅ Apple Pay initialization complete')
      } catch (error) {
        console.error('❌ Failed to initialize Apple Pay:', error)
        console.error('Error details:', error.response?.data || error.message)
        this.applePayAvailable = false
      }
    },

    async loadApplePayLauncher() {
      return new Promise((resolve, reject) => {
        const container = document.getElementById('bridgerpay-wallet-container')

        if (!container) {
          console.error('❌ BridgerPay wallet container not found!')
          reject(new Error('BridgerPay wallet container not found'))
          return
        }

        console.log('📦 Container found:', container)

        const script = document.createElement('script')
        script.src = 'https://checkout.bridgerpay.com/v2/launcher'
        script.setAttribute('data-cashier-key', this.applePayCashierKey)
        script.setAttribute('data-cashier-token', this.applePayCashierToken)
        script.setAttribute('data-button-mode', 'wallet')
        script.setAttribute('data-container', 'bridgerpay-wallet-container')
        script.id = 'bridgerpay-launcher-script'

        // Append script to container so BridgerPay renders the button there
        container.appendChild(script)

        script.onload = () => {
          console.log('✅ BridgerPay launcher script loaded')
          setTimeout(() => {
            console.log('🔍 Container contents:', container.innerHTML)
            console.log(
              '🔍 All wallet elements:',
              document.querySelectorAll('[class*="wallet"]')
            )
            console.log(
              '🔍 All BP elements:',
              document.querySelectorAll('[class*="bp-"]')
            )
          }, 3000)
          resolve()
        }

        script.onerror = () => {
          console.error('❌ Failed to load BridgerPay launcher script')
          reject(new Error('Failed to load Apple Pay launcher'))
        }
      })
    },

    setupApplePayEventListeners() {
      const checkoutKey = this.applePayCashierKey

      const addListener = (eventName, handler) => {
        window.addEventListener(eventName, handler)
        this.applePayEventListeners.push({ event: eventName, handler })
      }

      // Wallet button is ready
      addListener(`[bp][checkout:${checkoutKey}]:wallet-button-is-ready`, e => {
        console.log('🍎 Apple Pay button is ready:', e.detail)
      })

      // Wallet is open - triggered when wallet overlay appears
      addListener(`[bp][checkout:${checkoutKey}]:wallet-open`, e => {
        console.log('🍎 Apple Pay wallet opened:', e.detail)
      })

      // Wallet is processing payment
      addListener(
        `[bp][checkout:${checkoutKey}]:wallet-processing-payment`,
        e => {
          console.log('🍎 Apple Pay processing payment:', e.detail)
          this.processing = true
        }
      )

      // Wallet result - transaction result
      addListener(`[bp][checkout:${checkoutKey}]:wallet-result`, e => {
        console.log('🍎 Apple Pay result:', e.detail)
        const result = e.detail

        if (result?.status === 'approved' || result?.status === 'authorized') {
          this.handleApplePaySuccess(result)
        } else {
          this.handleApplePayError(result)
        }
      })

      // No wallet provider found in route
      addListener(
        `[bp][checkout:${checkoutKey}]:no-wallet-provider-found`,
        e => {
          console.log('🍎 No wallet provider found:', e.detail)
          this.applePayAvailable = false
        }
      )

      // User device not supported
      addListener(
        `[bp][checkout:${checkoutKey}]:wallet-provider-cannot-be-used`,
        e => {
          console.log('🍎 Wallet provider cannot be used:', e.detail)
          this.applePayAvailable = false
        }
      )

      // Wallet cancelled by user
      addListener(`[bp][checkout:${checkoutKey}]:wallet-cancelled`, e => {
        console.log('🍎 Apple Pay cancelled by user:', e.detail)
        this.processing = false
      })

      console.log(
        `🎧 Registered ${this.applePayEventListeners.length} listeners for ${checkoutKey}`
      )
    },

    customizeApplePayButton() {
      const applePayButton = document.querySelector('.wallet-apple-pay')

      if (applePayButton) {
        applePayButton.style.setProperty(
          '-apple-pay-button-type',
          'buy',
          'important'
        )
        applePayButton.style.setProperty(
          '-apple-pay-button-style',
          'black',
          'important'
        )
        applePayButton.setAttribute('lang', 'en')
      }
    },

    async handleApplePaySuccess(detail) {
      this.processing = true
      try {
        const response = await axios.post('/api/bridgerpay/apple-pay/confirm', {
          payment_intent_id: this.intentId,
          ...detail,
        })

        this.$emit('payment-success', {
          user: {
            email: detail?.user?.email || this.email || this.initialEmail,
          },
          paymentMethod: 'apple_pay',
          reservation: response.data.reservation,
        })
      } catch (error) {
        console.error('Apple Pay confirmation failed:', error)
        this.handleApplePayError({
          error: error.response?.data?.error || 'Payment confirmation failed',
        })
      } finally {
        this.processing = false
      }
    },

    handleApplePayError(detail) {
      this.processing = false
      this.formError =
        detail?.error ||
        'Apple Pay payment failed. Please try another payment method.'
    },
  },
}
</script>

<style scoped>
.field {
  border-color: var(--v-secondary-base);
  border-width: 1px;
  border-radius: 4px;
  border-style: solid;
  padding: 10px;
  font-size: 14px;
}
.w-100 {
  width: 100%;
}
.flex-1 {
  flex: 1;
}
.gap-3 {
  grid-gap: 7px;
}
.bridger-iframe {
  width: 100%;
  height: 600px;
  border: none;
  border-radius: 4px;
}

/* Apple Pay Styles */
.apple-pay-container {
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.wallet-divider {
  display: flex;
  align-items: center;
  width: 100%;
  margin: 20px 0;
}

.wallet-divider::before,
.wallet-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background-color: var(--v-secondary-base);
}

.wallet-divider span {
  padding: 0 15px;
  color: var(--v-secondary-base);
  font-size: 14px;
  white-space: nowrap;
}

#apple-pay-button-container {
  margin-top: 15px;
  min-height: 50px;
}

.apple-pay-button-wrapper {
  display: block;
  width: 100%;
}
</style>
